/**
 * <FxRate> — 환율 (USD/KRW + DXY)
 *
 * 한국 투자자한테 가장 중요한 macro indicator 중 하나.
 * 데이터: GET /api/ticker/market/fx → { usdkrw: {...}, dxy: {...} }
 */
"use client";

import { useEffect, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";

interface FxQuote {
  symbol: string;
  label: string;
  price: number | null;
  changePercent: number | null;
  change: number | null;
}

interface FxData {
  usdkrw: FxQuote | null;
  dxy: FxQuote | null;
}

const REFRESH_MS = 60 * 1000;

function fmtPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(2)}%`;
}

function fmtPrice(v: number | null, decimals = 2): string {
  if (v == null || !Number.isFinite(v)) return "—";
  return v.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

// 사용자 결정 #11 fix: 통화별 국기 (circle-flags CDN)
//   backend symbol = "KRW=X", label = "USD/KRW" 같이 형식 다양 → 둘 다 시도 + 부분 매칭
const FLAG_BY_KEY: Record<string, string[]> = {
  USDKRW: ["us", "kr"],
  KRWUSD: ["us", "kr"],
  KRW:    ["us", "kr"],   // backend "KRW=X" → "KRW"
  USDJPY: ["us", "jp"],
  JPY:    ["us", "jp"],
  EURUSD: ["eu", "us"],
  EUR:    ["eu", "us"],
  GBPUSD: ["gb", "us"],
  GBP:    ["gb", "us"],
  USDCNY: ["us", "cn"],
  CNY:    ["us", "cn"],
  DXY:    ["us"],
  UUP:    ["us"],
};
function getFlagCodes(symbol?: string, label?: string): string[] {
  const tries = [symbol, label]
    .filter(Boolean)
    .map((s) => String(s).replace(/[^A-Z]/gi, "").toUpperCase());
  for (const t of tries) {
    if (FLAG_BY_KEY[t]) return FLAG_BY_KEY[t];
    // 부분 매칭 (KRWX → KRW)
    for (const key of Object.keys(FLAG_BY_KEY)) {
      if (t.includes(key)) return FLAG_BY_KEY[key];
    }
  }
  return [];
}

function Row({ q }: { q: FxQuote | null }) {
  const pct = q?.changePercent ?? null;
  const up = pct != null && pct > 0;
  const down = pct != null && pct < 0;
  const flags = getFlagCodes(q?.symbol, q?.label);
  return (
    <div className="dw-fx-row">
      <div className="dw-fx-label">
        {flags.length > 0 && (
          <span className="dw-fx-flags" aria-hidden="true">
            {flags.map((c, i) => (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                key={i}
                src={`https://hatscripts.github.io/circle-flags/flags/${c}.svg`}
                width={16}
                height={16}
                alt=""
                loading="lazy"
                className="dw-fx-flag-img"
              />
            ))}
          </span>
        )}
        <span>{q?.label || "—"}</span>
      </div>
      <div className="dw-fx-price">{fmtPrice(q?.price ?? null)}</div>
      <div className={`dw-fx-pct ${up ? "up" : down ? "down" : ""}`}>{fmtPct(pct)}</div>
    </div>
  );
}

export function FxRate(_props: WidgetProps) {
  const [data, setData] = useState<FxData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        const res = await fetch("/api/ticker/market/fx", { credentials: "include" });
        if (!res.ok) {
          if (mountedRef.current) { setError("로드 실패"); setLoading(false); }
          return;
        }
        const d = await res.json();
        if (mountedRef.current) {
          setData(d);
          setError(null);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) { setError("네트워크 오류"); setLoading(false); }
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { mountedRef.current = false; clearInterval(t); };
  }, []);

  return (
    <div className="dw-body dw-fx">
      <div className="dw-head"><span className="dw-title">환율</span></div>
      {error ? (
        <div className="dw-fx-error">{error}</div>
      ) : loading && !data ? (
        <div className="dw-fx-empty">로딩 중…</div>
      ) : (
        <div className="dw-fx-list">
          <Row q={data?.usdkrw ?? null} />
          <Row q={data?.dxy ?? null} />
        </div>
      )}
    </div>
  );
}

export const fxRateMeta: WidgetMeta = {
  type: "fx-rate",
  name: "환율",
  description: "USD/KRW · 달러인덱스 (DXY) · 한국 투자자 macro 1번지표",
  icon: "💱",
  category: "market",
  defaultSize: { w: 3, h: 3 },
  availableSizes: [
    { w: 3, h: 3 },
    { w: 4, h: 3 },
    { w: 3, h: 4 },
  ],
  minW: 2,
  maxW: 6,
  minH: 2,
  maxH: 5,
  component: FxRate,
};
